/**
 * Navigation configuration helpers for review-pending sub-tabs.
 * @module reviewPendingNavConfig
 */

import {
  HealthCareFacilityModel,
  isHealthFacilityFeatureEnabled,
  normalizeReviewBoStatusesFromFacilityValue,
} from "../data-model/healthFacility";
import { swapUnderscoreHyphen } from "../utils/swapUnderscoreWithHypen";

/** A single navigation entry for a review-pending sub-tab. */
export interface ReviewPendingNavEntry {
  /** Route ID in the form "review-pending/<hyphenated-auth-status>". */
  id: string;
  /** Display label sourced from the facility's Firebase config. */
  label: string;
  /** Original auth_status value used for filtering and count tracking. */
  masterAuthStatus: string;
  /** Expected BO status ids on save for this tab (from facility `review_pending_config`). */
  reviewBoStatuses?: string[];
}

/**
 * Derives route id from auth_status: "review-pending/<hyphenated-auth_status>".
 */
export const reviewPendingIdFromAuthStatus = (authStatus: string): string =>
  `review-pending/${swapUnderscoreHyphen(authStatus)}`;

/**
 * Returns Review Pending nav entries from the facility's Firebase config.
 * Each entry: id and masterAuthStatus derived from auth_status; label from Firebase.
 */
export const getReviewPendingNavEntriesForFacility = (
  facility: HealthCareFacilityModel | undefined,
): ReviewPendingNavEntry[] => {
  if (
    !facility ||
    !isHealthFacilityFeatureEnabled(facility, "enable_review_mode")
  ) {
    return [];
  }
  const config = facility.review_pending_config;
  if (!config?.length) return [];

  return config.map((item) => ({
    id: reviewPendingIdFromAuthStatus(item.auth_status),
    label: item.label,
    masterAuthStatus: item.auth_status,
    reviewBoStatuses: normalizeReviewBoStatusesFromFacilityValue(
      item.review_bo_status as string | string[] | undefined,
    ),
  }));
};
